/**
 * MDX Provider
 *
 * Provides custom components for MDX content rendering.
 * Maps standard HTML elements to styled React components.
 */

import { MDXProvider as BaseMDXProvider } from '@mdx-js/react';
import { ReactNode } from 'react';

// Custom code block that replaces the default pre/code rendering
function CodeBlock({ children, className }: { children?: string; className?: string }) {
  // Extract language from className (e.g., "language-javascript" -> "javascript")
  const language = className?.replace('language-', '') || 'text';
  const code = typeof children === 'string' ? children.trim() : '';

  return (
    <div className="not-prose my-4 rounded-lg overflow-hidden bg-slate-900">
      {language !== 'text' && (
        <div className="px-4 py-2 bg-slate-800 border-b border-slate-700">
          <span className="text-xs font-mono uppercase tracking-wider text-slate-400">
            {language}
          </span>
        </div>
      )}
      <pre className="overflow-x-auto p-4 text-sm">
        <code className="text-slate-100 font-mono">{code}</code>
      </pre>
    </div>
  );
}

// Wrapper for pre elements - delegates to CodeBlock via children
function Pre({ children }: { children?: ReactNode }) {
  // If children is a code element, extract its props
  if (children && typeof children === 'object' && 'props' in children) {
    const codeElement = children as { props: { children?: string; className?: string } };
    return <CodeBlock {...codeElement.props} />;
  }
  // Fallback for non-code pre elements
  return (
    <pre className="not-prose my-4 p-4 rounded-lg bg-slate-900 text-slate-100 overflow-x-auto text-sm font-mono">
      {children}
    </pre>
  );
}

// Inline code styling
function InlineCode({ children }: { children?: ReactNode }) {
  return (
    <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 text-sm font-mono">
      {children}
    </code>
  );
}

// Code component handles both inline and block code
function Code({ className, children }: { className?: string; children?: ReactNode }) {
  // If it has a className (language-*), it's a code block handled by Pre
  // If no className, it's inline code
  if (className) {
    return <code className={className}>{children}</code>;
  }
  return <InlineCode>{children}</InlineCode>;
}

const components = {
  pre: Pre,
  code: Code,
};

interface MDXProviderProps {
  children: ReactNode;
}

export default function MDXProvider({ children }: MDXProviderProps) {
  return (
    <BaseMDXProvider components={components}>
      {children}
    </BaseMDXProvider>
  );
}
